import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import Button from 'reactstrap/lib/Button';
import Card from 'reactstrap/lib/Card';
import { get } from 'lodash-es';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import {
  buildTrimSummariesPath,
  InventoryCoreConfigurationsModel,
} from 'client/data/models/inventory-core-configurations';
import { ConsumerReviewsEntities } from 'client/data/models/consumer-reviews';
import { buildRecommendedTrimPath, EditorialReviewModel } from 'client/data/models/editorial-review';
import { VehicleEntities } from 'client/data/models/vehicle-v2';
import { VisitorEntities, VisitorModel } from 'client/data/models/visitor';
import { PAGE_DISPLAY_NAME, URL_PATH } from 'site-modules/consumer-reviews/pages/consumer-reviews/consumer-reviews';
import { ConsumerReviewsParams } from 'site-modules/shared/utils/consumer-reviews/params';
import { getLastWordPlural } from 'client/utils/plural';
import { isUnknownZip } from 'site-modules/shared/utils/location';
import { getUrlPathFromParams } from 'site-modules/shared/utils/core-breadcrumbs-builder';
import { getPublicationStateForMakeModel } from 'site-modules/shared/utils/publication-states';
import { srpLinkBuilder } from 'site-modules/shared/utils/srp-link-constructor';
import { getParamsFromVehicle } from 'site-modules/shared/utils/core-page/params';
import { Link } from 'site-modules/shared/components/link/link';
import { PhotoflipperButtonAsync } from 'site-modules/shared/components/photoflipper-core/photoflipper-button/photoflipper-button-async';
import { PhotoflipperImage } from 'site-modules/shared/components/photoflipper-image/photoflipper-image';
import { InternationalZipPickerCta } from 'site-modules/shared/components/international-zip-picker/international-zip-picker-cta';
import { CoreBreadcrumbs } from 'site-modules/shared/components/core-breadcrumbs/core-breadcrumbs';
import { MoreAboutMMYLink } from 'site-modules/shared/components/more-about-mmy-link/more-about-mmy-link';
import { SrpPhotoBox } from 'site-modules/shared/components/srp-photo-box/srp-photo-box';
import { ConsumerReviewSummary } from 'site-modules/consumer-reviews/components/consumer-review-summary/consumer-review-summary';
import { TrimsCta } from 'site-modules/shared/components/core-page/trims/trims-cta';
import { LeadFormButton } from 'site-modules/consumer-reviews/components/lead-form-button/lead-form-button';

import './consumer-reviews-intro.scss';

export function ConsumerReviewsIntroUI({
  params,
  vehicle,
  addLoading = undefined,
  vehicleFilter = null,
  reviewsFilter = null,
  displayVehicle = null,
  recommendedTrimSlug = null,
  visitorLocation = null,
  hasLeadFormDealers = false,
  isMobile = false,
}) {
  const {
    make: { slug: makeSlug, name: makeName },
    model: { slug: modelSlug, name: modelName },
    submodels: { name: submodelName },
    year,
    pubStates,
  } = vehicle;
  const { isUsed, isDealerlessOem } = params;
  const isInternational = isUnknownZip(visitorLocation);

  const vehicleParams = getParamsFromVehicle(vehicle);
  const showLeadForm = !isDealerlessOem && hasLeadFormDealers;

  return (
    <div className="consumer-reviews-intro">
      <CoreBreadcrumbs
        className="ps-0"
        vehicle={vehicle}
        breadcrumbsParams={{
          params,
          pageDisplayName: PAGE_DISPLAY_NAME,
          urlPath: getUrlPathFromParams(params, URL_PATH),
        }}
      />
      <Row className="mb-1_5 mb-xl-0">
        <Col xs={12} md={isUsed ? 8 : 7} lg={8} className="mb-1 mb-md-0" data-tracking-parent="edm-entry-summary">
          <h1 className="heading-3 mb-1">{displayVehicle} Consumer Reviews</h1>
          <MoreAboutMMYLink
            vehicle={vehicle}
            className={classnames('d-inline-block', {
              'mb-1_5': !isUsed,
              'mb-0_75 mb-md-1_5': isUsed,
            })}
            linkClassName={null}
            linkToReview={isUsed}
            dataTrackingId="go_to_core_review"
            creativeId="edm-entry-summary"
            stickyCreativeId="edm-entry-summary-dock-top"
            buttonFragment={
              !isUsed && <LeadFormButton vehicle={vehicle} isDealerlessOem={isDealerlessOem} isMobile={isMobile} />
            }
          />
          {isUsed && isMobile && (
            <PhotoflipperButtonAsync makeSlug={makeSlug} modelSlug={modelSlug} year={year}>
              <Button color="link" size="lg" className="p-0 fw-normal text-transform-none mb-1_5">
                View Photos &&nbsp;Videos
                <i className="icon-arrow-right4 text-primary-darker d-inline-block size-10 ms-0_25" aria-hidden />
              </Button>
            </PhotoflipperButtonAsync>
          )}
          <Row>
            {!isMobile && (
              <Col xs={12} lg={6}>
                <PhotoflipperImage
                  params={params}
                  vehicle={vehicle}
                  displayVehicle={displayVehicle}
                  iconClassName="mt-0_25 ms-0_25"
                  hasEditorialChoiceBadge={isMobile}
                  disableTrademarkCharacter
                />
              </Col>
            )}
            <Col xs={12} lg={6}>
              <ConsumerReviewSummary
                className={classnames({
                  'd-lg-flex desktop-reviews': !isMobile,
                  'mb-2': isUsed && isMobile,
                })}
                vehicle={vehicle}
                isMMYBased={false}
                params={params}
                showHeader={false}
                ratingStarLinkout={false}
                clickableRatingDistribution
                vehicleFilter={vehicleFilter}
                reviewsFilter={reviewsFilter}
                addLoading={addLoading}
                hideSummaryLinks={isUsed && isMobile}
              />
              {isUsed && isMobile && (
                <div className="text-center px-2_5">
                  <Button
                    tag={Link}
                    to={srpLinkBuilder({
                      make: makeName,
                      model: modelName,
                      submodel: submodelName,
                      year,
                      inventorytype: getPublicationStateForMakeModel(pubStates),
                    })}
                    rel="nofollow"
                    color="primary-b"
                    size="lg"
                    className="w-100 py-0_5 rounded-8 fw-normal text-transform-none"
                    data-tracking-id="crr_all_for_sale"
                  >
                    See All <span className="visually-hidden">{getLastWordPlural(modelName)}</span> for Sale
                  </Button>
                </div>
              )}
            </Col>
          </Row>
        </Col>
        {!isMobile && (
          <Col xs={12} md={isUsed ? 4 : 5} lg={4}>
            {!isUsed && (
              <div className="trim-summary-section modular rounded-12 bg-cool-gray-90 px-1 py-1">
                <h2 className="heading-3 mb-1_5">Pricing</h2>
                <div
                  className="intro-trims-content px-1 px-lg-0_5 px-xl-1 py-1 border-0 rounded-8 bg-white"
                  data-tracking-parent="edm-entry-new_model_core_consumer_reviews-model-summary"
                >
                  <TrimsCta
                    params={vehicleParams}
                    vehicle={vehicle}
                    recommendedTrimSlug={recommendedTrimSlug}
                    visitorLocation={visitorLocation}
                    showLeadForm={showLeadForm}
                    isDealerlessOem={isDealerlessOem}
                    isMobile={isMobile}
                  />
                </div>
              </div>
            )}
            {isUsed && (
              <div>
                {isInternational ? (
                  <InternationalZipPickerCta
                    params={{ ...params, isUsed }}
                    vehicle={vehicle}
                    btnTitle="See all for sale"
                  />
                ) : (
                  <Card className="px-1 px-lg-0_5 px-xl-1 py-1">
                    <div className="heading-4 mb-1_5">Used {modelName} for sale</div>
                    <SrpPhotoBox
                      params={{ ...params, isUsed }}
                      vehicle={vehicle}
                      isMobile={isMobile}
                      hasDesktopFallback
                      hideBuyOnline
                    />
                  </Card>
                )}
              </div>
            )}
          </Col>
        )}
      </Row>
    </div>
  );
}

ConsumerReviewsIntroUI.propTypes = {
  params: ConsumerReviewsParams.isRequired,
  vehicle: VehicleEntities.MakeModelSubmodelYear.isRequired,
  addLoading: PropTypes.func,
  reviewsFilter: ConsumerReviewsEntities.ReviewsFilter,
  vehicleFilter: ConsumerReviewsEntities.VehicleFilter,
  displayVehicle: PropTypes.string,
  recommendedTrimSlug: PropTypes.string,
  visitorLocation: VisitorEntities.Location,
  hasLeadFormDealers: PropTypes.bool,
  isMobile: PropTypes.bool,
};

export const ConsumerReviewsIntro = connectToModel(ConsumerReviewsIntroUI, {
  recommendedTrimSlug: bindToPath(
    ({ params }) => !params.isUsed && buildRecommendedTrimPath(params),
    EditorialReviewModel
  ),
  hasLeadFormDealers: bindToPath(
    ({ params, vehicle }) => !params.isUsed && buildTrimSummariesPath(getParamsFromVehicle(vehicle)),
    InventoryCoreConfigurationsModel,
    trims => !!get(trims, '[0].hasLeadFormDealers')
  ),
  visitorLocation: bindToPath('location', VisitorModel),
});
